import CircularProgress from '@material-ui/core/CircularProgress'
import FormControl from '@material-ui/core/FormControl'
import Menu from '@material-ui/core/Menu'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import * as d3 from 'd3-hierarchy'
import _ from 'lodash'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import { MOBILE_WIDTH } from 'pared/constants/styles'

import { useVariables } from '../variables'
import MenuItem, { IValueType } from './MenuItem'
import useMenuAnchorEl from './hooks/useMenuAnchorEl'
import useSearch from './hooks/useSearch'
import useValidateItems, {
  IValidateItemsOptionsType,
} from './hooks/useValidateItems'

export interface IPropsType {
  type: 'select'
  value: string
  onChange: string
  options: string | IValueType[]
  disable?: string
  label?: string
  renderValue?: `<%- ${string} %>`
  multiple?: true
  width?: string
  labelWidth?: string
  margin?: string
  search?: boolean
}

const StyledFormControl = styled(FormControl)<{
  styleMargin?: string
}>`
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 25px;
  ${({ styleMargin }) => (!styleMargin ? '' : `margin: ${styleMargin};`)}
`

const Label = styled.div<{
  width?: string
}>`
  display: inline-block;
  font-family: Lexend-Regular;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  ${({ width }) => (!width ? '' : `width: ${width};`)};
`

const StyledTextField = styled(TextField)<{
  width?: string
  disabled?: boolean
}>`
  min-width: 150px;
  ${({ width }) => (!width ? '' : `width: ${width};`)};
  font-family: Lexend-Regular;
  cursor: pointer;
  margin: 0px;

  @media ${MOBILE_WIDTH} {
    width: 100%;
  }

  > *,
  input {
    font-family: Lexend-Regular;
    cursor: ${({ disabled }) => (disabled ? 'initial' : 'pointer')};
  }

  .MuiInputBase-root {
    border-radius: 0;
  }
`

const StyledMenu = styled(Menu)<{
  minWidth: number
  searching: boolean
}>`
  .MuiList-root {
    min-width: ${({ minWidth }) => minWidth}px;
    ${({ searching }) => (!searching ? '' : `max-height: 30vh;`)}
  }
`

const Select = ({
  label,
  multiple,
  renderValue = '<%- values?.[0]?.map(v => v.displayName).join(" / ") %>',
  width,
  labelWidth,
  margin,
  search,
  ...props
}: IPropsType) => {
  const { variables, template } = useVariables()
  const value = useMemo(
    (): IValidateItemsOptionsType['value'] => _.get(variables, props.value),
    [props, variables],
  )
  const { onChange, options, disable } = useMemo(
    () => ({
      onChange: _.get(
        variables,
        props.onChange,
      ) as IValidateItemsOptionsType['onChange'],
      options: d3.stratify<IValueType>()([
        { id: 'root' } as IValueType,
        ...(typeof props.options === 'string'
          ? _.get(variables, props.options)
          : props.options),
      ]),
      disable: props.disable && _.get(variables, props.disable),
    }),
    [props, variables],
  )
  const { value: menuValue, onChange: menuOnChange } = useValidateItems({
    value,
    onChange,
    options,
    multiple,
  })
  const { menuAnchorEl, open, onOpen, onClose } = useMenuAnchorEl(
    !options.children,
  )
  const searching = Boolean(search) && open
  const {
    inputRef,
    value: searchValue,
    onChange: onSearchChange,
    searchOptions,
  } = useSearch(searching, options)

  return (
    <StyledFormControl styleMargin={margin} variant="outlined" margin="dense">
      {!label ? null : <Label width={labelWidth}>{template(label)}</Label>}

      <StyledTextField
        ref={inputRef}
        width={width}
        value={
          searching
            ? searchValue
            : _.template(renderValue)({
                values: menuValue,
              }).replace(/&amp;/g, '&')
        }
        onChange={onSearchChange}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {!options.children && !disable ? (
                <CircularProgress size={20} />
              ) : (
                <ArrowDropDownIcon />
              )}
            </InputAdornment>
          ),
          readOnly: !search,
        }}
        onClick={onOpen}
        variant="outlined"
        margin="dense"
        size="small"
        placeholder={(() => {
          if (searching) return ''
          if (disable) return 'No Data Found'
          return 'Loading...'
        })()}
        disabled={disable}
      />

      <StyledMenu
        minWidth={inputRef.current?.getBoundingClientRect().width || 0}
        searching={searching}
        open={open}
        onClose={onClose}
        disableAutoFocus={search}
        disableEnforceFocus={search}
        anchorEl={menuAnchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        anchorReference="anchorEl"
      >
        {(searching ? searchOptions : options.children)?.map((option) => (
          <MenuItem
            key={option.data.id}
            onChange={menuOnChange}
            onClose={onClose}
            option={option}
            selectedIds={value.filter((v) => v[0] === option.data.id)}
            multiple={multiple || false}
          />
        ))}
      </StyledMenu>
    </StyledFormControl>
  )
}

export default React.memo(Select) as unknown as typeof Select
